import firebase from 'firebase/app';

import firestoreServices from 'src/services/firestore.service';
import insurersServices from 'src/services/insurers.services';
import accidentsServices from 'src/services/accidents.services';
import specialtiesServices from 'src/services/specialties.services';
import operatorsServices from 'src/services/users.services';
import { COMPLETED, OPEN, PENDING } from 'src/constants';

const worksFirestoreServices = firestoreServices('works');

const getDuration = (startDate, endDate) => {
  let duration = [];
  for (
    let min = startDate.getMinutes() + 15, hour = startDate.getHours();
    min < endDate.getMinutes() || hour <= endDate.getHours() - 1;
    min = min + 15
  ) {
    if (min === 60) {
      min = 0;
      hour = hour + 1;
    }
    startDate.setMinutes(min);
    startDate.setHours(hour);
    if (
      !(
        startDate.getMinutes() === endDate.getMinutes() &&
        startDate.getHours() === endDate.getHours()
      )
    )
      duration.push(firebase.firestore.Timestamp.fromDate(startDate));
  }
  return duration;
};

const getSubcollection = async doc => {
  let data = doc.data();
  for (let key in data) {
    const item = data[key];
    if (typeof item === 'object' && item && item.parent) {
      await firestoreServices(item.parent.id).get(item.id, async itemData => {
        data[key] = itemData;
      });
    }
  }
  return { id: doc.id, ...data };
};

const listElements = queryResult => {
  const elements = queryResult.docs.map(async (doc, _) => {
    return await getSubcollection(doc);
  });

  return Promise.all(elements);
};

export default {
  ...worksFirestoreServices,
  add: (data, onSuccess, onError = _ => {}) => {
    let duration = getDuration(new Date(data.start), new Date(data.end));
    if (
      typeof data.accidentId === 'string' ||
      data.accidentId instanceof String
    ) {
      data.accident = accidentsServices.docRef(data.accidentId);
    }
    if (
      typeof data.insurerId === 'string' ||
      data.insurerId instanceof String
    ) {
      data.insurer = insurersServices.docRef(data.insurerId);
    }
    if (
      typeof data.specialtyId === 'string' ||
      data.specialtyId instanceof String
    ) {
      data.specialty = specialtiesServices.docRef(data.specialtyId);
    }
    if (
      typeof data.operatorId === 'string' ||
      data.operatorId instanceof String
    ) {
      data.operator = operatorsServices.docRef(data.operatorId);
    }
    if (data.scheduledAt !== undefined) {
      data.scheduledAt = firebase.firestore.Timestamp.fromDate(
        new Date(data.scheduledAt)
      );
    }

    data.start = firebase.firestore.Timestamp.fromDate(new Date(data.start));
    data.end = firebase.firestore.Timestamp.fromDate(new Date(data.end));
    data.isActive = true;
    data.status = 'OPEN';

    return worksFirestoreServices.add(
      {
        duration: duration,
        ...data
      },
      onSuccess,
      onError
    );
  },
  update: (id, data, onSuccess, onError = _ => {}) => {
    let duration = getDuration(new Date(data.start), new Date(data.end));
    if (data.start) {
      data.start = firebase.firestore.Timestamp.fromDate(new Date(data.start));
    }
    if (data.end) {
      data.end = firebase.firestore.Timestamp.fromDate(new Date(data.end));
    }
    if (
      typeof data.insurerId === 'string' ||
      data.insurerId instanceof String
    ) {
      data.insurer = insurersServices.docRef(data.insurerId);
    }
    if (
      typeof data.accidentId === 'string' ||
      data.accidentId instanceof String
    ) {
      data.accident = accidentsServices.docRef(data.accidentId);
    }
    if (
      typeof data.specialtyId === 'string' ||
      data.specialtyId instanceof String
    ) {
      data.specialty = specialtiesServices.docRef(data.specialtyId);
    }
    if (
      typeof data.operatorId === 'string' ||
      data.operatorId instanceof String
    ) {
      data.operator = operatorsServices.docRef(data.operatorId);
    }
    return worksFirestoreServices.update(
      id,
      {
        duration: duration,
        ...data
      },
      onSuccess,
      onError
    );
  },
  withNoStatus: (accidentId, status, onResult, onError) => {
    let query = worksFirestoreServices.collectionRef();
    return query
      .where('accidentId', '==', accidentId)
      .where('status', '!=', status)
      .get()
      .then(async queryResult => onResult(await listElements(queryResult)));
  },
  byOperatorAndStatus: (operatorId, status, onResult, onError) => {
    let query = worksFirestoreServices.collectionRef();
    return query
      .where('operatorId', '==', operatorId)
      .where('status', '==', status)
      .orderBy('start', 'asc')
      .get()
      .then(async queryResult => onResult(await listElements(queryResult)));
  },
  calendarQueryOrdered: (
    accidentId,
    operatorId,
    dateGt,
    dateLt,
    onResult,
    onError
  ) => {
    let query = worksFirestoreServices.collectionRef();
    if (operatorId) {
      query = query.where('operatorId', '==', operatorId);
    }
    return query
      .where('accidentId', '==', accidentId)
      .where('start', '>=', dateGt)
      .where('start', '<=', dateLt)
      .orderBy('start', 'asc')
      .get()
      .then(async queryResult => onResult(await listElements(queryResult)));
  },
  calendarQueryAll: (
    operatorId,
    dateGt,
    dateLt,
    onResult,
    onError = _ => {}
  ) => {
    let query = worksFirestoreServices.collectionRef();

    if (operatorId) {
      query = query.where('operatorId', '==', operatorId);
    }
    return query
      .where('start', '>=', dateGt)
      .where('start', '<=', dateLt)
      .where('status', 'in', [OPEN, COMPLETED, PENDING])
      .get()
      .then(async queryResult => onResult(await listElements(queryResult)));
  },
  byOperatorAndDatetime: (
    startDate,
    endDate,
    operatorId,
    workId = null,
    onResult,
    onError
  ) => {
    startDate = firebase.firestore.Timestamp.fromDate(new Date(startDate));
    endDate = firebase.firestore.Timestamp.fromDate(new Date(endDate));
    let results = [];
    const addResults = (r = []) => {
      results =
        results + r.filter(r => r.status !== 'CANCELED' && r.id !== workId);
    };
    const getQuery = (field, startCond, endCond) =>
      worksFirestoreServices
        .collectionRef()
        .where('operatorId', '==', operatorId)
        .where(field, startCond, startDate)
        .where(field, endCond, endDate)
        .get()
        .then(async queryResult => addResults(await listElements(queryResult)));

    const getDurationQuery = date =>
      worksFirestoreServices
        .collectionRef()
        .where('operatorId', '==', operatorId)
        .where('duration', 'array-contains', date)
        .get()
        .then(async queryResult => addResults(await listElements(queryResult)));
    return Promise.all([
      getQuery('start', '>=', '<'),
      getQuery('end', '>', '<='),
      getDurationQuery(startDate) // Case when one time range is inside another.
      // Firestore doesn't allow query by >, < on different fields. :(
    ]).then(async () => await onResult(results));
  }
};
